import {
  BookmarkCheck,
  BookmarkPlus,
  CheckCircle2,
  Circle,
  Clock,
  ExternalLink,
  EyeOff,
  Flag,
  Layers,
  Plus,
  RefreshCw,
  SplitSquareHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { PageHeader } from '../components/AppShell';
import { PageCaption } from '../components/PageCaption';
import { Topbar } from '../components/Topbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import {
  DEFAULT_FEEDS,
  type FeedItem,
  type ItemStatus,
  useCombinedFeeds,
  useFeed,
  useItemStatuses,
  useSetItemStatus,
} from '../lib/api/newsCompare';
import {
  DEFAULT_EXPIRY_HOURS,
  EXPIRY_OPTIONS,
  STORAGE_EXPIRY_KEY,
  type BoardItem,
  useAddBoardItem,
  useClearBoard,
  useNewsBoard,
  useRemoveBoardItem,
  useUpdateBoardExpiry,
} from '../lib/api/newsBoard';
import { fmtRelative } from '../lib/date';

const MAX_COLUMNS = 6;
const STORAGE_FEEDS = 'news-compare-active-feeds';
const STORAGE_TAB = 'news-compare-tab';

type StatusKey = Exclude<ItemStatus, 'none'> | 'none';

const STATUS_CONFIG: Record<
  StatusKey,
  { label: string; Icon: React.ComponentType<{ className?: string }>; iconClass: string }
> = {
  none:     { label: 'None',     Icon: Circle,       iconClass: 'text-ink-subtle' },
  assessed: { label: 'Assessed', Icon: CheckCircle2, iconClass: 'text-green-500' },
  ignored:  { label: 'Ignore',   Icon: EyeOff,       iconClass: 'text-ink-muted' },
  flagged:  { label: 'Flag',     Icon: Flag,         iconClass: 'text-amber-500' },
};

const STATUS_KEYS = Object.keys(STATUS_CONFIG) as StatusKey[];

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function expiryClass(expiresAt: string | null): string {
  if (!expiresAt) return 'text-ink-subtle/70';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'text-red-500';
  if (diff < 24 * 3_600_000) return 'text-amber-500';
  if (diff < 3 * 24 * 3_600_000) return 'text-amber-400/80';
  return 'text-ink-subtle/70';
}

function expiryLabel(expiresAt: string | null): string {
  if (!expiresAt) return 'No expiry';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return '< 1h left';
  if (h < 24) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
}

// ---------------------------------------------------------------------------

interface StatusPickerProps {
  status: ItemStatus;
  onChange: (s: ItemStatus) => void;
}

function StatusPicker({ status, onChange }: StatusPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { Icon, iconClass } = STATUS_CONFIG[status];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`mt-0.5 rounded p-0.5 transition-colors hover:bg-surface-muted ${iconClass}`}
        title="Set status"
      >
        <Icon className="h-3.5 w-3.5" />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-20 mt-1 w-36 rounded-lg border border-line bg-surface py-1 shadow-lg">
          {STATUS_KEYS.map((key) => {
            const { label, Icon: OptionIcon, iconClass: optClass } = STATUS_CONFIG[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => { onChange(key); setOpen(false); }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-surface-muted ${status === key ? 'font-semibold' : ''}`}
              >
                <OptionIcon className={`h-3.5 w-3.5 ${optClass}`} />
                {label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface SaveButtonProps {
  saved: boolean;
  onSave: () => void;
}

function SaveButton({ saved, onSave }: SaveButtonProps): JSX.Element {
  const [flash, setFlash] = useState(false);

  const handleSave = () => {
    if (saved) return;
    onSave();
    setFlash(true);
    setTimeout(() => setFlash(false), 1500);
  };

  if (saved || flash) {
    return (
      <span className="shrink-0 rounded-md p-1.5 text-brand" title="Saved to board">
        <BookmarkCheck className="h-4 w-4" />
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={handleSave}
      className="shrink-0 rounded-md p-1.5 text-ink-subtle/40 transition-all hover:bg-brand/10 hover:text-brand"
      title="Save to board"
    >
      <BookmarkPlus className="h-4 w-4" />
    </button>
  );
}

// ---------------------------------------------------------------------------

function AddSourceMenu({
  available,
  onAdd,
  disabled,
}: {
  available: { url: string; label: string }[];
  onAdd: (url: string) => void;
  disabled: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePreset = (url: string) => {
    onAdd(url);
    setOpen(false);
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = customUrl.trim();
    if (!trimmed) return;
    try { new URL(trimmed); } catch { return; }
    onAdd(trimmed);
    setCustomUrl('');
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2.5 py-1 text-xs text-ink-subtle transition-colors hover:border-brand hover:text-brand disabled:opacity-40"
      >
        <Plus className="h-3 w-3" />
        Add source
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1.5 w-56 rounded-lg border border-line bg-surface shadow-lg">
          {available.length > 0 ? (
            <div className="border-b border-line py-1">
              {available.map((f) => (
                <button
                  key={f.url}
                  type="button"
                  onClick={() => handlePreset(f.url)}
                  className="flex w-full items-center px-3 py-2 text-sm text-ink hover:bg-surface-muted"
                >
                  {f.label}
                </button>
              ))}
            </div>
          ) : null}
          <form onSubmit={handleCustomSubmit} className="flex items-center gap-1.5 p-2">
            <Input
              placeholder="Custom RSS URL…"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              ref={inputRef}
              className="h-7 flex-1 text-xs"
            />
            <Button type="submit" size="sm" className="h-7 px-2 text-xs" disabled={!customUrl.trim()}>
              Add
            </Button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface FeedColumnProps {
  url: string;
  label?: string;
  cmsType?: string;
  onRemove: () => void;
  onSave: (item: FeedItem, sourceLabel: string) => void;
  savedUrls: Set<string>;
  statuses: Record<string, ItemStatus>;
  onSetStatus: (itemUrl: string, status: ItemStatus) => void;
}

function FeedColumn({ url, label, cmsType, onRemove, onSave, savedUrls, statuses, onSetStatus }: FeedColumnProps): JSX.Element {
  const { data, isLoading, isError, error, refetch, isFetching } = useFeed(url, cmsType);
  const sourceLabel = label ?? data?.feedTitle ?? new URL(url).hostname;
  const itemCount = data?.items.length ?? 0;

  return (
    <div className="flex min-w-0 flex-1 flex-col rounded-xl border border-line bg-surface shadow-card">
      {/* Column header */}
      <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-semibold text-ink" title={label ?? data?.feedTitle ?? url}>
            {sourceLabel}
          </p>
          {!isLoading && itemCount > 0 ? (
            <span className="shrink-0 rounded-full bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium text-ink-subtle">
              {itemCount}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded p-1.5 text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1.5 text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink"
            title="Remove column"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Spinner label="Loading feed…" />
          </div>
        ) : isError ? (
          <p className="p-4 text-sm text-red-500">
            {(error as Error).message ?? 'Failed to load feed'}
          </p>
        ) : itemCount === 0 ? (
          <p className="p-4 text-sm text-ink-muted">No items found in feed.</p>
        ) : (
          <ul className="divide-y divide-line">
            {[...(data?.items ?? [])]
              .sort((a, b) => {
                if (!a.publishedAt && !b.publishedAt) return 0;
                if (!a.publishedAt) return 1;
                if (!b.publishedAt) return -1;
                return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
              })
              .map((item, i) => {
                const status = statuses[item.url] ?? 'none';
                const contentClass =
                  status === 'assessed' ? 'opacity-40' :
                  status === 'ignored'  ? 'opacity-25' : '';
                const rowClass =
                  status === 'flagged' ? 'border-l-2 border-l-amber-400 pl-[10px]' : '';
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: rss items have no stable id
                  <li key={i} className={`group flex items-start gap-2 px-3 py-2.5 hover:bg-surface-muted/60 ${rowClass}`}>
                    <StatusPicker
                      status={status}
                      onChange={(s) => onSetStatus(item.url, s)}
                    />
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex min-w-0 flex-1 items-start gap-1.5 ${contentClass}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span
                          className={`line-clamp-2 text-sm font-medium hover:text-brand ${
                            status === 'assessed' ? 'line-through text-ink-subtle' : 'text-ink'
                          }`}
                        >
                          {item.title}
                        </span>
                        {item.description && status === 'none' ? (
                          <span className="mt-0.5 line-clamp-2 block text-xs text-ink-subtle">
                            {item.description}
                          </span>
                        ) : null}
                        {item.publishedAt ? (
                          <span className="mt-1 block text-[11px] text-ink-subtle">
                            {fmtRelative(item.publishedAt)}
                          </span>
                        ) : null}
                      </span>
                      <ExternalLink className="mt-0.5 h-3 w-3 shrink-0 text-ink-subtle opacity-0 group-hover:opacity-100" />
                    </a>
                    <SaveButton
                      saved={savedUrls.has(item.url)}
                      onSave={() => onSave(item, sourceLabel)}
                    />
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface CombinedItem extends FeedItem {
  sourceLabel: string;
}

function localDateStr(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function todayStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
}

function dateGroupLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (localDateStr(iso) === `${yesterday.getFullYear()}-${yesterday.getMonth()}-${yesterday.getDate()}`) {
    return 'Yesterday';
  }
  return d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function CombinedItemRow({
  item,
  status,
  onSetStatus,
  onSave,
  saved,
}: {
  item: CombinedItem;
  status: ItemStatus;
  onSetStatus: (s: ItemStatus) => void;
  onSave: () => void;
  saved: boolean;
}): JSX.Element {
  const contentClass =
    status === 'assessed' ? 'opacity-40' :
    status === 'ignored'  ? 'opacity-25' : '';
  const rowClass =
    status === 'flagged' ? 'border-l-2 border-l-amber-400 pl-[10px]' : '';

  return (
    <li className={`group flex items-start gap-2 px-4 py-3 hover:bg-surface-muted/60 ${rowClass}`}>
      <StatusPicker status={status} onChange={onSetStatus} />
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex min-w-0 flex-1 flex-col gap-0.5 ${contentClass}`}
      >
        <span
          className={`line-clamp-2 text-sm font-medium hover:text-brand ${
            status === 'assessed' ? 'line-through text-ink-subtle' : 'text-ink'
          }`}
        >
          {item.title}
        </span>
        {item.description && status === 'none' ? (
          <span className="line-clamp-2 text-xs text-ink-subtle">{item.description}</span>
        ) : null}
        <span className="flex items-center gap-1.5 text-[11px] text-ink-subtle">
          <span className="font-semibold text-brand/70">{item.sourceLabel}</span>
          {item.publishedAt ? (
            <>
              <span aria-hidden>·</span>
              <span>{fmtRelative(item.publishedAt)}</span>
            </>
          ) : null}
        </span>
      </a>
      <ExternalLink className="mt-0.5 h-3 w-3 shrink-0 text-ink-subtle opacity-0 group-hover:opacity-100" />
      <SaveButton saved={saved} onSave={onSave} />
    </li>
  );
}

function CombinedView({
  activeUrls,
  statuses,
  onSetStatus,
  onSave,
  savedUrls,
}: {
  activeUrls: string[];
  statuses: Record<string, ItemStatus>;
  onSetStatus: (url: string, status: ItemStatus) => void;
  onSave: (item: FeedItem, sourceLabel: string) => void;
  savedUrls: Set<string>;
}): JSX.Element {
  const activeFeeds = activeUrls.map((url) => DEFAULT_FEEDS.find((f) => f.url === url) ?? { url });
  const feedResults = useCombinedFeeds(activeFeeds);
  const isAnyLoading = feedResults.some((r) => r.isLoading);

  const combined: CombinedItem[] = feedResults.flatMap((result, i) => {
    const url = activeUrls[i];
    if (!url) return [];
    const label = DEFAULT_FEEDS.find((f) => f.url === url)?.label ?? new URL(url).hostname;
    return (result.data?.items ?? []).map((item) => ({ ...item, sourceLabel: label }));
  });

  const sorted = [...combined].sort((a, b) => {
    if (!a.publishedAt && !b.publishedAt) return 0;
    if (!a.publishedAt) return 1;
    if (!b.publishedAt) return -1;
    return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  });

  const today = todayStr();
  const todayItems = sorted.filter((it) => it.publishedAt && localDateStr(it.publishedAt) === today);
  const earlierItems = sorted.filter((it) => !it.publishedAt || localDateStr(it.publishedAt) !== today);

  const earlierGroups: { label: string; items: CombinedItem[] }[] = [];
  for (const item of earlierItems) {
    const label = item.publishedAt ? dateGroupLabel(item.publishedAt) : 'Undated';
    const last = earlierGroups[earlierGroups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      earlierGroups.push({ label, items: [item] });
    }
  }

  const totalCount = sorted.length;

  return (
    <div className="space-y-4">
      {/* Status bar */}
      <div className="flex items-center gap-2 text-[11px] text-ink-subtle">
        {isAnyLoading ? (
          <>
            <RefreshCw className="h-3 w-3 animate-spin" />
            <span>Loading feeds…</span>
          </>
        ) : totalCount > 0 ? (
          <span>
            {totalCount} item{totalCount === 1 ? '' : 's'} across {activeUrls.length} source{activeUrls.length === 1 ? '' : 's'}
          </span>
        ) : (
          <span>No items found.</span>
        )}
      </div>

      {totalCount === 0 && !isAnyLoading ? null : (
        <>
          {todayItems.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-brand/25 bg-brand/[0.03] shadow-card">
              <div className="flex items-center gap-2 border-b border-brand/20 bg-brand/5 px-4 py-2.5">
                <span className="live-dot" />
                <p className="text-xs font-semibold uppercase tracking-wider text-brand">Today</p>
                <span className="ml-auto rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">
                  {todayItems.length}
                </span>
              </div>
              <ul className="divide-y divide-brand/10">
                {todayItems.map((item, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: merged feed items have no stable id
                  <CombinedItemRow
                    key={i}
                    item={item}
                    status={statuses[item.url] ?? 'none'}
                    onSetStatus={(s) => onSetStatus(item.url, s)}
                    onSave={() => onSave(item, item.sourceLabel)}
                    saved={savedUrls.has(item.url)}
                  />
                ))}
              </ul>
            </div>
          ) : null}

          {earlierGroups.map((group) => (
            <div key={group.label} className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
              <div className="border-b border-line bg-surface-muted/50 px-4 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
                  {group.label}
                </p>
              </div>
              <ul className="divide-y divide-line-hair">
                {group.items.map((item, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: merged feed items have no stable id
                  <CombinedItemRow
                    key={i}
                    item={item}
                    status={statuses[item.url] ?? 'none'}
                    onSetStatus={(s) => onSetStatus(item.url, s)}
                    onSave={() => onSave(item, item.sourceLabel)}
                    saved={savedUrls.has(item.url)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Board panel
// ---------------------------------------------------------------------------

function BoardItemRow({
  item,
  onRemove,
  onUpdateExpiry,
}: {
  item: BoardItem;
  onRemove: () => void;
  onUpdateExpiry: (hours: number | null) => void;
}): JSX.Element {
  const [changeValue, setChangeValue] = useState('');

  return (
    <li className="border-b border-line-hair px-4 py-3 last:border-0 hover:bg-surface-muted/40">
      {/* Title + remove */}
      <div className="mb-1.5 flex items-start gap-2">
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0 flex-1 line-clamp-2 text-sm font-medium leading-snug text-ink hover:text-brand"
        >
          {item.title}
        </a>
        <button
          type="button"
          onClick={onRemove}
          className="mt-0.5 shrink-0 rounded p-0.5 text-ink-subtle/40 transition-colors hover:text-red-500"
          title="Remove from board"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Source + added */}
      <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px]">
        <span className="font-semibold text-brand/70">{item.sourceLabel}</span>
        <span className="text-ink-subtle">{fmtRelative(item.addedAt)}</span>
      </div>

      {/* Expiry */}
      <div className="flex items-center gap-1.5 text-[11px]">
        <Clock className={`h-3 w-3 shrink-0 ${expiryClass(item.expiresAt)}`} />
        <span className={expiryClass(item.expiresAt)}>{expiryLabel(item.expiresAt)}</span>
        <div className="ml-auto">
          <Select
            value={changeValue}
            onValueChange={(v) => {
              onUpdateExpiry(v === 'none' ? null : Number(v));
              setChangeValue('');
            }}
          >
            <SelectTrigger className="h-6 w-[90px] border-line/60 px-2 text-[10px]">
              <SelectValue placeholder="Change…" />
            </SelectTrigger>
            <SelectContent>
              {EXPIRY_OPTIONS.map((o) => (
                <SelectItem key={o.hours ?? 'none'} value={String(o.hours ?? 'none')} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </li>
  );
}

function BoardPanel({
  items,
  defaultExpiryHours,
  onDefaultExpiryChange,
  onRemove,
  onUpdateExpiry,
  onClear,
  onClose,
}: {
  items: BoardItem[];
  defaultExpiryHours: number | null;
  onDefaultExpiryChange: (hours: number | null) => void;
  onRemove: (id: string) => void;
  onUpdateExpiry: (id: string, hours: number | null) => void;
  onClear: () => void;
  onClose: () => void;
}): JSX.Element {
  const [confirmClear, setConfirmClear] = useState(false);

  const handleClear = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 3000);
      return;
    }
    onClear();
    setConfirmClear(false);
  };

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-l border-line bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-brand" />
          <span className="text-sm font-semibold text-ink">News Board</span>
          {items.length > 0 ? (
            <span className="rounded-full bg-brand/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand">
              {items.length}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          {items.length > 0 ? (
            <button
              type="button"
              onClick={handleClear}
              className={`rounded p-1 transition-colors ${
                confirmClear
                  ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                  : 'text-ink-subtle hover:bg-surface-muted hover:text-ink'
              }`}
              title={confirmClear ? 'Click again to clear all' : 'Clear all items'}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink"
            title="Close board"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Default expiry */}
      <div className="flex items-center gap-2 border-b border-line bg-surface-muted/40 px-4 py-2.5">
        <Clock className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
        <span className="text-[11px] text-ink-subtle">Save with expiry</span>
        <div className="ml-auto">
          <Select
            value={String(defaultExpiryHours ?? 'none')}
            onValueChange={(v) => onDefaultExpiryChange(v === 'none' ? null : Number(v))}
          >
            <SelectTrigger className="h-7 w-[110px] text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRY_OPTIONS.map((o) => (
                <SelectItem key={o.hours ?? 'none'} value={String(o.hours ?? 'none')} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
            <BookmarkPlus className="h-8 w-8 text-ink-subtle/30" />
            <p className="text-sm font-medium text-ink-muted">No stories saved yet</p>
            <p className="text-xs leading-relaxed text-ink-subtle">
              Click the bookmark icon on any headline to add it here.
            </p>
          </div>
        ) : (
          <ul>
            {items.map((item) => (
              <BoardItemRow
                key={item.id}
                item={item}
                onRemove={() => onRemove(item.id)}
                onUpdateExpiry={(hours) => onUpdateExpiry(item.id, hours)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function NewsDesk(): JSX.Element {
  const [activeUrls, setActiveUrls] = useState<string[]>(() =>
    readStorage<string[]>(STORAGE_FEEDS, [DEFAULT_FEEDS[0].url, DEFAULT_FEEDS[2].url]),
  );
  const [tab, setTab] = useState<'compare' | 'combined'>(
    () => readStorage<'compare' | 'combined'>(STORAGE_TAB, 'compare'),
  );
  const [boardOpen, setBoardOpen] = useState(false);
  const [defaultExpiryHours, setDefaultExpiryHours] = useState<number | null>(() => {
    const saved = readStorage<number | 'none'>(STORAGE_EXPIRY_KEY, DEFAULT_EXPIRY_HOURS ?? 'none');
    return saved === 'none' ? null : saved;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_EXPIRY_KEY, JSON.stringify(defaultExpiryHours ?? 'none'));
  }, [defaultExpiryHours]);

  const { data: statuses = {} } = useItemStatuses();
  const { mutate: setStatus } = useSetItemStatus();
  const { data: boardData } = useNewsBoard();
  const { mutate: addToBoard } = useAddBoardItem();
  const { mutate: removeFromBoard } = useRemoveBoardItem();
  const { mutate: updateExpiry } = useUpdateBoardExpiry();
  const { mutate: clearBoard } = useClearBoard();

  const boardItems = boardData?.items ?? [];
  const savedUrls = new Set(boardItems.map((i) => i.url));

  useEffect(() => {
    localStorage.setItem(STORAGE_FEEDS, JSON.stringify(activeUrls));
  }, [activeUrls]);

  useEffect(() => {
    localStorage.setItem(STORAGE_TAB, JSON.stringify(tab));
  }, [tab]);

  const addFeed = (url: string) => {
    const trimmed = url.trim();
    if (!trimmed || activeUrls.includes(trimmed) || activeUrls.length >= MAX_COLUMNS) return;
    try { new URL(trimmed); } catch { return; }
    setActiveUrls((prev) => [...prev, trimmed]);
  };

  const removeFeed = (url: string) => setActiveUrls((prev) => prev.filter((u) => u !== url));

  const handleSave = (item: FeedItem, sourceLabel: string) => {
    addToBoard({
      url: item.url,
      title: item.title,
      description: item.description,
      sourceLabel,
      publishedAt: item.publishedAt,
      expiresInHours: defaultExpiryHours,
    });
    if (!boardOpen) setBoardOpen(true);
  };

  const available = DEFAULT_FEEDS.filter((f) => !activeUrls.includes(f.url));

  return (
    <>
      <Topbar title="News Desk" />
      <PageHeader
        title="News Desk"
        subtitle="Monitor and curate the latest headlines from external sources"
      />
      <PageCaption id="news-desk" icon={SplitSquareHorizontal}>
        <strong className="text-ink">News desk</strong> — a single live feed of
        what the rest of Nigerian (and international) media is publishing right
        now, pulled from RSS + Premium Times scraping. Use it to spot stories
        that broke elsewhere before you covered them, then "Pin" the ones worth
        chasing into the News board for editor follow-up.
      </PageCaption>

      <div className="flex flex-1 items-stretch">
        {/* Main content */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 px-6 pb-10 pt-4">
          {/* Unified toolbar */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2 shadow-card">
            {/* Tab switcher — only shown when there are active sources */}
            {activeUrls.length > 0 ? (
              <>
                <div className="flex items-center rounded-md bg-surface-muted/70 p-0.5">
                  {(['compare', 'combined'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTab(t)}
                      className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                        tab === t
                          ? 'bg-surface text-ink shadow-sm'
                          : 'text-ink-muted hover:text-ink'
                      }`}
                    >
                      {t === 'compare' ? 'Compare' : 'Combined'}
                    </button>
                  ))}
                </div>
                <div className="h-4 w-px shrink-0 bg-line" />
              </>
            ) : null}

            {/* Active source chips + add menu */}
            <div className="flex flex-1 flex-wrap items-center gap-1.5">
              {activeUrls.map((url) => {
                const label = DEFAULT_FEEDS.find((f) => f.url === url)?.label ?? new URL(url).hostname;
                return (
                  <span
                    key={url}
                    className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-muted/50 py-0.5 pl-2.5 pr-1.5 text-xs font-medium text-ink"
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => removeFeed(url)}
                      className="rounded-full text-ink-subtle transition-colors hover:text-ink"
                      title={`Remove ${label}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
              <AddSourceMenu
                available={available}
                onAdd={addFeed}
                disabled={activeUrls.length >= MAX_COLUMNS}
              />
              {activeUrls.length >= MAX_COLUMNS ? (
                <span className="text-[11px] text-ink-subtle">Max {MAX_COLUMNS} sources</span>
              ) : null}
            </div>

            {/* Board toggle */}
            <button
              type="button"
              onClick={() => setBoardOpen((v) => !v)}
              className={`flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                boardOpen
                  ? 'bg-brand text-white'
                  : 'border border-line text-ink-muted hover:text-ink'
              }`}
              title="Toggle news board"
            >
              <Layers className="h-3.5 w-3.5" />
              Board
              {boardItems.length > 0 ? (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
                    boardOpen ? 'bg-white/20 text-white' : 'bg-brand/10 text-brand'
                  }`}
                >
                  {boardItems.length}
                </span>
              ) : null}
            </button>
          </div>

          {/* Content */}
          {activeUrls.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <p className="text-sm font-medium text-ink-muted">No sources added yet</p>
              <p className="text-xs text-ink-subtle">Use "Add source" above to start monitoring feeds.</p>
            </div>
          ) : tab === 'compare' ? (
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: `repeat(${activeUrls.length}, minmax(0, 1fr))` }}
            >
              {activeUrls.map((url) => {
                const feedDef = DEFAULT_FEEDS.find((f) => f.url === url);
                return (
                  <FeedColumn
                    key={url}
                    url={url}
                    label={feedDef?.label}
                    cmsType={feedDef?.cmsType}
                    onRemove={() => removeFeed(url)}
                    onSave={handleSave}
                    savedUrls={savedUrls}
                    statuses={statuses}
                    onSetStatus={(itemUrl, status) => setStatus({ url: itemUrl, status })}
                  />
                );
              })}
            </div>
          ) : (
            <CombinedView
              activeUrls={activeUrls}
              statuses={statuses}
              onSetStatus={(itemUrl, status) => setStatus({ url: itemUrl, status })}
              onSave={handleSave}
              savedUrls={savedUrls}
            />
          )}
        </div>

        {/* Board panel */}
        {boardOpen ? (
          <div className="sticky top-0 h-screen">
            <BoardPanel
              items={boardItems}
              defaultExpiryHours={defaultExpiryHours}
              onDefaultExpiryChange={setDefaultExpiryHours}
              onRemove={(id) => removeFromBoard(id)}
              onUpdateExpiry={(id, hours) => updateExpiry({ id, expiresInHours: hours })}
              onClear={() => clearBoard()}
              onClose={() => setBoardOpen(false)}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
